import React, { useState, useEffect, useMemo } from 'react';
import axios from '../../axios-base';
import { Col, Spinner, Alert, Button, Row } from 'reactstrap';
import CountUp from 'react-countup';
import DynamicIcon from '../UI/DynamicIcon/DynamicIcon';
import styles from './CampaignMetrics.module.scss';

// Allowed class whitelists (module scope to avoid re-creating per render)
const ALLOWED_BACKGROUND_CLASSES = new Set(['bg-light', 'bg-primary', 'bg-secondary']);
const ALLOWED_TEXT_COLORS = new Set(['text-primary', 'text-secondary', 'text-dark', 'text-light']);

// Normalize a single metric coming from the API into a safe shape for rendering
const normalizeMetric = (item, index) => {
    const backgroundClass = ALLOWED_BACKGROUND_CLASSES.has(item.backgroundClass) ? item.backgroundClass : 'bg-light';

    const iconColor = ALLOWED_TEXT_COLORS.has(item.iconColor) ? item.iconColor : 'text-primary';
    const valueColor = ALLOWED_TEXT_COLORS.has(item.valueColor) ? item.valueColor : 'text-primary';
    const labelColor = ALLOWED_TEXT_COLORS.has(item.labelColor) ? item.labelColor : 'text-dark';

    return {
        metricKey: item._id || `metric_${index}`,
        label: item.label ?? '',
        order: item.order ?? index + 1,
        isVisible: item.isVisible !== undefined ? item.isVisible : true,
        value: item.value ?? 0,
        unit: item.unit ?? '',
        format: item.format ?? 'number',
        icon: item.icon,
        formatting: item.formatting,
        backgroundClass,
        iconColor,
        valueColor,
        labelColor,
    };
};

const CampaignMetrics = ({ isOverlay }) => {
    const [metrics, setMetrics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const getCountUpProps = (metric) => {
        const endValue = Number(metric.value) || 0;
        const common = {
            end: endValue,
            duration: 1.6,
            separator: metric.formatting?.thousandsSeparator ? '.' : '',
            decimal: ',',
        };

        if (metric.format === 'currency' && metric.unit === '€') {
            return { ...common, suffix: ' €' };
        }

        if (metric.unit) {
            return { ...common, suffix: ` ${metric.unit}` };
        }

        return common;
    };

    useEffect(() => {
        const fetchCampaignMetrics = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axios.get('/metrics');
                const apiPayload = response?.data;

                // Backend returns a plain array of metrics
                const apiMetrics = Array.isArray(apiPayload) ? apiPayload : [];

                const normalizedMetrics = apiMetrics.map((item, index) => normalizeMetric(item, index));

                // Filter only visible metrics and sort by order
                const visibleMetrics = normalizedMetrics
                    .filter((metric) => metric.isVisible)
                    .sort((a, b) => a.order - b.order);

                setMetrics(visibleMetrics);
            } catch (err) {
                console.error('Error fetching campaign metrics:', err);
                setError('Failed to load campaign metrics');
            } finally {
                setLoading(false);
            }
        };

        fetchCampaignMetrics();
    }, []);

    // Derive the row background once from the first visible metric
    const rowBackgroundClass = useMemo(() => {
        if (isOverlay) return 'bg-transparent';
        return metrics.length > 0 ? metrics[0].backgroundClass : 'bg-light';
    }, [metrics, isOverlay]);

    if (error) {
        return (
            <Alert color="danger" className="text-center">
                <p className="mb-3">{error}</p>
                <Button color="secondary" onClick={() => window.location.reload()}>
                    Retry
                </Button>
            </Alert>
        );
    }

    if (!loading && (!metrics || metrics.length === 0)) {
        return null;
    }

    return (
        <Row className={`d-flex justify-content-center mx-0 ${rowBackgroundClass}`}>
            {metrics.map((metric) => {
                const countUpProps = getCountUpProps(metric);
                return (
                    <Col key={metric.metricKey} xs={4} md={3} className="p-3 text-md-start text-center">
                        <div className="d-flex flex-md-row flex-column align-items-center justify-content-center">
                            {metric.icon && (
                                <span className={`d-none d-md-inline-flex me-3 ${metric.iconColor}`}>
                                    <DynamicIcon name={metric.icon} size={40} />
                                </span>
                            )}
                            <div>
                                <p
                                    className={`fw-bold ${metric.valueColor} mb-0 ${styles.metricValue}`}
                                    style={{ lineHeight: '1em' }}
                                >
                                    {metric.icon && (
                                        <DynamicIcon
                                            name={metric.icon}
                                            size={20}
                                            className={`me-2 d-inline d-md-none ${metric.iconColor}`}
                                        />
                                    )}
                                    {loading ? (
                                        <Spinner size="sm" className="me-2" />
                                    ) : metric.value !== undefined && metric.value !== null ? (
                                        <CountUp key={`${metric.metricKey}-${metric.value}`} {...countUpProps} />
                                    ) : (
                                        '--'
                                    )}
                                </p>
                                <p className={`mb-0 ${metric.labelColor}`} style={{ lineHeight: '1em' }}>
                                    {metric.label}
                                </p>
                            </div>
                        </div>
                    </Col>
                );
            })}
        </Row>
    );
};

export default CampaignMetrics;
